<script setup lang="ts">
import { computed } from "vue";
import { useSettingsStore } from "@editor/store/settings.store";
import { formatRoundName } from "../simulationRound/useRoundTime";
import type { ConditionItem } from "./ConditionItem";

interface Props {
  startType: ConditionItem["activatorStartType"];
  endType: ConditionItem["activatorEndType"];
  start: ConditionItem["activatorStart"];
  end: ConditionItem["activatorEnd"];
  timeForm: "point" | "range";
  operator?: "!" | string | null;
}
const props = withDefaults(defineProps<Props>(), {
  operator: ""
});

const settingsStore = useSettingsStore();

function formatRoundIndex(index: number) {
  return formatRoundName(
    settingsStore.simulationDurationType,
    settingsStore.simulationRoundUnit,
    settingsStore.simulationRoundLength,
    index,
    settingsStore.simulationTimeStart,
    false
  );
}

const roundUnit = computed(() => settingsStore.simulationRoundUnit);

const usedTimeForm = computed(() => {
  if (props.timeForm == "point") return "point";

  if (props.endType == null) return "point";

  if (
    areStartAndEndDifferent(
      props.startType,
      props.start,
      props.endType,
      props.end
    )
  ) {
    return "range";
  }

  return "point";
});

function areStartAndEndDifferent(
  startType: string | null,
  start: number | null,
  endType: string | null,
  end: number | null
) {
  if (startType != endType) return true;
  if (start != end) return true;
  return false;
}
</script>

<template>
  <div v-if="startType != null">
    <!-- TIME -->

    <div v-if="usedTimeForm == 'range'">
      <strong v-if="endType == 'currentRound'">
        <template v-if="startType == 'relativeRound'">
          {{ $t(`labels.since.relative.${roundUnit}`, start || 0) }}
        </template>
        <template v-else-if="startType == 'absoluteRound'">
          {{ $t(`labels.since.word`) }}
          {{ formatRoundIndex(start || 0) }}
        </template>
      </strong>
      <i18n-t
        v-else
        :keypath="
          operator == '!'
            ? 'labels.conditionLabelRoundRangeNot'
            : 'labels.conditionLabelRoundRange'
        "
        scope="global"
      >
        <template #start>
          <strong v-if="startType == 'absoluteRound'">
            {{ formatRoundIndex(start || 0) }}
          </strong>
          <strong v-else-if="startType == 'relativeRound'">
            {{ $t(`timeAgo.${roundUnit}`, start || 0) }}
          </strong>
          <strong v-else-if="startType == 'currentRound'">
            {{ $t("times.currentRound") }}
          </strong>
          <strong v-else-if="startType == 'startRound'">
            {{ $t("times.startRound") }}
          </strong>
        </template>
        <template #end>
          <strong v-if="endType == 'absoluteRound'">
            {{ formatRoundIndex(end ?? (start || 0)) }}
          </strong>
          <strong v-else-if="endType == 'relativeRound'">
            {{ $t(`timeAgo.${roundUnit}`, end ?? (start || 0)) }}
          </strong>
          <strong v-else-if="endType == 'currentRound'">
            {{ $t("times.currentRound") }}
          </strong>
        </template>
      </i18n-t>
    </div>

    <strong v-else>
      <template v-if="startType == 'currentRound'">
        <template v-if="timeForm == 'range'">
          {{ $t("times.inCurrentRound") }}
        </template>
      </template>
      <template v-else-if="startType == 'startRound'">
        {{ $t("times.inStartRound") }}
      </template>
      <template v-else-if="startType == 'absoluteRound'">
        {{ $t("words.in") }}
        <strong>{{ formatRoundIndex(start || 0) }}</strong>
      </template>
      <template v-else>
        {{ $t(`timeAgo.${roundUnit}`, start || 0) }}
      </template>
    </strong>
  </div>
</template>
